import { Component, OnInit } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToasterService } from '../../../providers/toaster/toaster.service';
import { PermissionService } from './permission.service';
import { Roles, permissionRoles } from '../../../providers/static-data';

interface PermissionRow {
  id: string;
  label: string;
  level: number;
  isPermission: boolean;
  permissionName?: string;
  hasChildren?: boolean;
  isComponent?: boolean;
  menuId?: number;
  menuName?: string;
  menuActionId?: number;
  parentMenuActionId?: number | null;
}

@Component({
  selector: 'app-permissions',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './permissions.component.html',
  styleUrl: './permissions.component.css',
})
export class PermissionsComponent implements OnInit {
  menus: any[] = [];

  // from dummy-data.ts
  permissionRoles = permissionRoles;
  

 
  permissionState: Record<string, boolean> = {};
  rolePermissionJson: any = null;
rolePermissions: Record<string, any> = {};
  expandedParent: number | null = null;
  isSavingPermissions = false;
selectedMainMenus: Record<string, boolean> = {};

  constructor(
    private menuService: PermissionService,
    private toasterService: ToasterService,
  ) {}

  ngOnInit() {
    this.fetchMenus();
    this.loadAssignableMenus();
    this.loadRolePermissionJson();
  }





 loadRolePermissionJson() {

  this.permissionRoles.forEach(role => {

    this.menuService
      .getRoleMenuPermissionsByRoleId(role.value)
      .subscribe({

        next: (res: any) => {

          const data = res?.body?.data || res?.data;

          this.rolePermissions[role.value] = data;

          this.tryInitializePermissionState();
        },

        error: () => {
          this.rolePermissions[role.value] = {
            main_menu_ids: [],
            master_uac_action_ids: []
          };
        }

      });

  });

}


fetchMenus() {
  this.menuService.getMenus().subscribe({
    next: (res: any) => {
      const rawMenus = res?.body?.data?.menus || res?.data || [];

      this.menus = (rawMenus || []).map((menu: any) => {

        const directChildren = Array.isArray(menu?.uac_items)
          ? this.mapUacTreeToPermissionSubtree(menu.uac_items)
          : [];

        // ✅ Build subMenuChildren WITH their UAC action children
        // NOTE: Removed special-casing for Monthly Scheduler / Weekly Planner.
        // We must map *all* uac_items so that every menu_action_id permission is present.
        const subMenuChildren = (menu?.children || []).map((child: any) => {
          const uacChildren = Array.isArray(child?.uac_items)
            ? this.mapUacTreeToPermissionSubtree(child.uac_items)
            : [];


          return {
            id: child?.menu_id,
            menu_name: child?.menu_name,
            children: uacChildren,  // ✅ UAC actions attached here
          };
        });

        // children for getVisiblePermissionRows on main menu (no submenu case)
        const menuChildren = subMenuChildren.map((child: any) => ({
          id: child.id,
          menu_name: child.menu_name,
          children: child.children,
          permissions: [],
        }));

        return {
          id: menu?.menu_id,
          menu_name: menu?.menu_name,
          slug: menu?.slug,
          url: menu?.url,
          type: menu?.type,
          sequence_no: menu?.sequence_no,
          subMenuChildren,           // ✅ used in HTML for sub-menu accordion
          children: [...directChildren, ...menuChildren],
        };
      });

      this.initializePermissionStateFromJson();
    },
    error: () => {
      this.toasterService.setToaster('error', 'Error', 'Failed to load menus');
    },
  });
}
  private tryInitializePermissionState() {
    if (!this.menus?.length) return;
   if (!Object.keys(this.rolePermissions).length) return; 
    this.initializePermissionStateFromJson();
  }

initializePermissionStateFromJson() {
  const roles = this.rolePermissions;

  this.permissionRoles.forEach((role) => {
    const roleData = roles[role.value];
    const mainMenuIds: number[] = roleData?.main_menu_ids ?? [];
    const mainMenuSet = new Set(mainMenuIds.map(Number));

    this.menus.forEach((menu) => {
      // Top-level menu
      this.selectedMainMenus[`${role.value}::${menu.id}`] = mainMenuSet.has(Number(menu.id));

      // ← ADD: Sub-menus (Schedule > Monthly Scheduler, Settings > Permission, etc.)
      (menu.subMenuChildren || []).forEach((child: any) => {
        this.selectedMainMenus[`${role.value}::${child.id}`] = mainMenuSet.has(Number(child.id));
      });
    });
  });

  // Permission Toggle Initialization (unchanged)
  this.getAllPermissionRows().forEach((row) => {
    this.permissionRoles.forEach((role) => {
      const actionIds = roles?.[role.value]?.master_uac_action_ids ?? [];
      const actionSet = new Set(actionIds.map(Number));
      const key = this.permissionStateKey(role.value, row);
      this.permissionState[key] = actionSet.has(Number(row.menuActionId));
    });
  });
}
private isValidNumber(value: any): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

private getSelectedActionIds(roleId: string): number[] {
  // Strictly return only master_uac_action_ids (menu_action_id) and never menu ids.
  // Also dedupe to avoid sending repeated menu_action_id values.
  const ids = this.getAllPermissionRows()
    .filter((row) => {
      if (!row.isPermission) return false; // exclude submenu/main header rows
      if (!this.isPermissionEnabled(row, roleId)) return false;
      if (!this.canTogglePermission(row, roleId)) return false;
      const id = row.menuActionId;
      // Guard against undefined/NaN/string cases
      return id !== undefined && id !== null && this.isValidNumber(Number(id));
    })
    .map((row) => Number(row.menuActionId));

  return Array.from(new Set(ids));
}


expandedSubMenu: number | null = null;

viewSubMenu(subMenuId: number) {
  this.expandedSubMenu = this.expandedSubMenu === subMenuId ? null : subMenuId;
}

isSubMenuExpanded(subMenuId: number): boolean {
  return this.expandedSubMenu === subMenuId;
}

  viewMenu(menuId: number) {
    this.expandedParent = this.expandedParent === menuId ? null : menuId;
  }

  isMenuExpanded(menuId: number): boolean {
    return this.expandedParent === menuId;
  }
toggleMainMenu(menuId: number, event: Event) {
  const input = event.target as HTMLInputElement;
  this.selectedMainMenus[menuId] = input.checked;
}
toggleSubMenuForRole(subMenuId: number, roleId: string, event: Event) {
  const input = event.target as HTMLInputElement;
  const checked = input.checked;

  this.selectedMainMenus[`${roleId}::${subMenuId}`] = checked;

  // ✅ Cascade checked state to all actions under this sub-menu
  this.setActionsForSubMenu(roleId, subMenuId, checked);
}
toggleMainMenuForRole(menuId: number, roleId: string, event: Event) {
  const input = event.target as HTMLInputElement;
  const checked = input.checked;

  // Main toggle
  this.selectedMainMenus[`${roleId}::${menuId}`] = checked;

  // Cascade checked state to all sub-menus
  const menu = this.menus.find((m) => m.id === menuId);
  (menu?.subMenuChildren || []).forEach((child: any) => {
    this.selectedMainMenus[`${roleId}::${child.id}`] = checked;
  });

  // ✅ Cascade to all actions under this main menu
  this.setActionsForMainMenu(roleId, menuId, checked);
}


private getSelectedMainMenuIdsForRole(roleId: string): number[] {
  const selectedIds: number[] = [];

  this.menus.forEach((menu) => {
    // Top-level menu
    if (this.selectedMainMenus[`${roleId}::${menu.id}`]) {
      selectedIds.push(menu.id);
    }

    // ← ADD: Sub-menus
    (menu.subMenuChildren || []).forEach((child: any) => {
      if (this.selectedMainMenus[`${roleId}::${child.id}`]) {
        selectedIds.push(child.id);
      }
    });
  });

  return selectedIds;
}

  getVisiblePermissionRows(menu: any): PermissionRow[] {
  return this.getTreeRows(menu.children || []);
}

  private setActionsForSubMenu(roleId: string, subMenuId: number, checked: boolean) {
    // Find this submenu inside menus structure (A submenu is defined as menu.subMenuChildren[*]).
    for (const menu of this.menus) {
      const subMenu = (menu.subMenuChildren || []).find((s: any) => Number(s.id) === Number(subMenuId));
      if (!subMenu) continue;

      const rows = this.getVisiblePermissionRows(subMenu).filter((r) => r.isPermission);
      rows.forEach((row) => {
        if (!row.permissionName) return;
        this.permissionState[this.permissionStateKey(roleId, row)] = checked;
      });
      return;
    }
  }

  private setActionsForMainMenu(roleId: string, mainMenuId: number, checked: boolean) {
    const menu = this.menus.find((m) => Number(m.id) === Number(mainMenuId));
    if (!menu) return;

    // Direct actions under main menu (CASE 2 in template)
    const directRows = this.getVisiblePermissionRows(menu).filter((r) => r.isPermission);
    directRows.forEach((row) => {
      if (!row.permissionName) return;
      this.permissionState[this.permissionStateKey(roleId, row)] = checked;
    });

    // Actions under sub-menus (CASE 1 in template)
    (menu.subMenuChildren || []).forEach((subMenu: any) => {
      this.setActionsForSubMenu(roleId, subMenu.id, checked);
    });
  }

  private areAnyActionsEnabledForSubMenu(roleId: string, subMenuId: number): boolean {
    for (const menu of this.menus) {
      const subMenu = (menu.subMenuChildren || []).find((s: any) => Number(s.id) === Number(subMenuId));
      if (!subMenu) continue;

      return this.getVisiblePermissionRows(subMenu)
        .filter((r) => r.isPermission && !!r.permissionName)
        .some((row) => this.isPermissionEnabled(row, roleId));
    }

    return false;
  }

  private areAnyActionsEnabledForMainMenu(roleId: string, mainMenuId: number): boolean {
    const menu = this.menus.find((m) => Number(m.id) === Number(mainMenuId));
    if (!menu) return false;

    const directEnabled = this.getVisiblePermissionRows(menu)
      .filter((r) => r.isPermission && !!r.permissionName)
      .some((row) => this.isPermissionEnabled(row, roleId));

    if (directEnabled) return true;

    return (menu.subMenuChildren || []).some((subMenu: any) =>
      this.areAnyActionsEnabledForSubMenu(roleId, subMenu.id)
    );
  }

  // A submenu is the accordion parent that renders the action row.
  private getParentSubMenuIdForAction(actionRow: PermissionRow, roleId: string): number | null {
    // We determine membership by scanning which submenu contains this action row's menuActionId.
    if (!actionRow.menuActionId) return null;

    for (const menu of this.menus) {
      for (const subMenu of menu.subMenuChildren || []) {
        const rows = this.getVisiblePermissionRows(subMenu).filter((r) => r.isPermission);
        const found = rows.some((r) => Number(r.menuActionId) === Number(actionRow.menuActionId));
        if (found) return Number(subMenu.id);
      }
    }

    return null;
  }

  private getParentMainMenuIdForSubMenu(subMenuId: number): number | null {
    const menu = this.menus.find((m) => (m.subMenuChildren || []).some((s: any) => Number(s.id) === Number(subMenuId)));
    return menu ? Number(menu.id) : null;
  }

  private getParentMainMenuIdForAction(actionRow: PermissionRow): number | null {
    if (!actionRow.menuActionId) return null;

    for (const menu of this.menus) {
      const directMatch = this.getVisiblePermissionRows(menu)
        .some((row) => Number(row.menuActionId) === Number(actionRow.menuActionId));

      if (directMatch) return Number(menu.id);
    }

    return null;
  }

  private findPermissionRowByActionId(menuActionId: number): PermissionRow | null {
    return this.getAllPermissionRows().find((row) => Number(row.menuActionId) === Number(menuActionId)) ?? null;
  }

  private getChildRowsForComponent(parentRow: PermissionRow): PermissionRow[] {
    if (!parentRow.menuActionId) return [];

    const allRows = this.getAllPermissionRows();
    const children: PermissionRow[] = [];
    const collect = (parentActionId: number) => {
      allRows
        .filter((row) => Number(row.parentMenuActionId) === Number(parentActionId))
        .forEach((row) => {
          children.push(row);
          if (row.menuActionId) {
            collect(row.menuActionId);
          }
        });
    };

    collect(parentRow.menuActionId);
    return children;
  }

  private setChildActionsForComponent(parentRow: PermissionRow, roleId: string, checked: boolean) {
    this.getChildRowsForComponent(parentRow).forEach((childRow) => {
      if (!childRow.permissionName) return;
      this.permissionState[this.permissionStateKey(roleId, childRow)] = checked;
    });
  }

  private selectMenuParentsForAction(row: PermissionRow, roleId: string) {
    const parentSubMenuId = this.getParentSubMenuIdForAction(row, roleId);
    if (parentSubMenuId != null) {
      this.selectedMainMenus[`${roleId}::${parentSubMenuId}`] = true;

      const parentMainMenuId = this.getParentMainMenuIdForSubMenu(parentSubMenuId);
      if (parentMainMenuId != null) {
        this.selectedMainMenus[`${roleId}::${parentMainMenuId}`] = true;
      }

      return;
    }

    const parentMainMenuId = this.getParentMainMenuIdForAction(row);
    if (parentMainMenuId != null) {
      this.selectedMainMenus[`${roleId}::${parentMainMenuId}`] = true;
    }
  }

  private flattenMenuRows(items: any[], level: number): PermissionRow[] {
    let rows: PermissionRow[] = [];

    items.forEach((item) => {
      const submenuId = `submenu-${item.id}`;

      if (item.children?.length) {
  rows.push({
    id: submenuId,
    label: item.menu_name,
    level,
    isPermission: false,
    hasChildren: true,
    menuId: item.id,
    menuName: item.menu_name,
  });
}

      if (item.permissions?.length) {
        item.permissions.forEach((permission: string) => {
          if (permission) {
            rows.push(this.createPermissionRow(permission, item, level + 1));
          }
        });
      }

      if (item.children?.length) {
        rows = rows.concat(this.flattenMenuRows(item.children, level + 1));
      }
    });

    return rows;
  }

  private createPermissionRow(
    permission: string,
    menu: any,
    level: number,
  ): PermissionRow {
    return {
      id: `${menu.id}-${permission}`,
      label: permission,
      permissionName: permission,
      level,
      isPermission: true,
      hasChildren: false,
      menuId: menu.id,
      menuName: menu.menu_name,
    };
  }

  private getAllPermissionRows(): PermissionRow[] {
  const rows: PermissionRow[] = [];

  this.menus.forEach((menu) => {
    // Direct UAC rows on main menu
    rows.push(
      ...this.getVisiblePermissionRows(menu).filter((r) => r.isPermission)
    );

    // ✅ UAC rows inside sub-menus
    (menu.subMenuChildren || []).forEach((subMenu: any) => {
      rows.push(
        ...this.getVisiblePermissionRows(subMenu).filter((r) => r.isPermission)
      );
    });
  });

  return rows;
}

  isRoleApplicable(roleId: string, row: PermissionRow): boolean {
    if (!row.isPermission) return false;
    return roleId !== '4' || row.isPermission;
  }

  stateKey(roleId: string, permissionName: string): string {
    return `${roleId}::${permissionName}`;
  }

  private permissionStateKey(roleId: string, row: PermissionRow): string {
    return row.menuActionId != null
      ? `${roleId}::action:${row.menuActionId}`
      : this.stateKey(roleId, row.permissionName ?? row.id);
  }

  isPermissionEnabled(row: PermissionRow, roleId: string): boolean {
    if (!row.isPermission || !row.permissionName) return false;
    if (row.parentMenuActionId && !this.canTogglePermission(row, roleId)) return false;
    const key = this.permissionStateKey(roleId, row);
    return this.permissionState[key] ?? roleId !== '4';
  }

  toggleRolePermission(row: PermissionRow, roleId: string, event: Event) {
    if (!row.isPermission || !row.permissionName) return;

    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked && !this.canTogglePermission(row, roleId)) {
      input.checked = false;
      return;
    }

    this.permissionState[this.permissionStateKey(roleId, row)] = checked;

    if (row.isComponent || row.hasChildren) {
      this.setChildActionsForComponent(row, roleId, checked);
    }

    if (checked) {
      this.selectMenuParentsForAction(row, roleId);
    }

    // ✅ When an action is turned OFF, auto-disable parent sub-menu/main-menu if no enabled child actions remain.
    if (!checked) {
      const parentSubMenuId = this.getParentSubMenuIdForAction(row, roleId);
      if (parentSubMenuId != null) {
        const anyEnabled = this.areAnyActionsEnabledForSubMenu(roleId, parentSubMenuId);
        this.selectedMainMenus[`${roleId}::${parentSubMenuId}`] = anyEnabled;

        const parentMainMenuId = this.getParentMainMenuIdForSubMenu(parentSubMenuId);
        if (parentMainMenuId != null) {
          const anyEnabledMain = this.areAnyActionsEnabledForMainMenu(roleId, parentMainMenuId);
          this.selectedMainMenus[`${roleId}::${parentMainMenuId}`] = anyEnabledMain;
        }
      } else {
        const parentMainMenuId = this.getParentMainMenuIdForAction(row);
        if (parentMainMenuId != null) {
          const anyEnabledMain = this.areAnyActionsEnabledForMainMenu(roleId, parentMainMenuId);
          this.selectedMainMenus[`${roleId}::${parentMainMenuId}`] = anyEnabledMain;
        }
      }
    }
  }

  canTogglePermission(row: PermissionRow, roleId: string): boolean {
    if (!row.parentMenuActionId) return true;

    const parentRow = this.findPermissionRowByActionId(row.parentMenuActionId);
    return parentRow
      ? this.isPermissionEnabled(parentRow, roleId) && this.canTogglePermission(parentRow, roleId)
      : true;
  }

  

  savePermissions() {
    this.isSavingPermissions = true;

    const payloads = this.permissionRoles.map((r) => ({
  role_id: Number(r.value),
  main_menu_ids:
    this.getSelectedMainMenuIdsForRole(r.value),
  master_uac_action_ids:
    this.getSelectedActionIds(r.value),
  is_active: true,
}));

    let doneCount = 0;
    let anyFailed = false;

    payloads.forEach((payload) => {
      this.menuService.createMenu(payload).subscribe({
        next: () => {
          doneCount++;
          if (doneCount === payloads.length && !anyFailed) {
            this.isSavingPermissions = false;
            this.toasterService.setToaster(
              'success',
              'Saved',
              'Role permissions saved successfully'
            );
          }
        },
        error: () => {
          anyFailed = true;
          doneCount++;
          if (doneCount === payloads.length) {
            this.isSavingPermissions = false;
            this.toasterService.setToaster(
              'error',
              'Error',
              'Failed to save role permissions'
            );
          }
        },
      });
    });
  }




  

  private roleIdToAlias(roleId: string): string {
    const match = (Roles ?? []).find((r: any) => String(r?.value) === String(roleId));
    return match?.aliase ?? match?.alias ?? '';
  }

 private mapUacTreeToPermissionSubtree(nodes: any[], parentMenuActionId: number | null = null): any[] {
  if (!Array.isArray(nodes)) return [];

  return nodes.map((node: any) => ({
    id: node.menu_action_id,
    menu_action_id: node.menu_action_id,
    parent_menu_action_id: parentMenuActionId,
    is_component: Number(node.is_component) === 1,
    menu_id: node.menu_id,
    menu_name: node.name,
    permissionName: node.name,
    children: this.mapUacTreeToPermissionSubtree(node.children || [], node.menu_action_id)
  }));
}

getTreeRows(nodes: any[], level = 0): PermissionRow[] {
  let rows: PermissionRow[] = [];

  nodes.forEach((node) => {
    rows.push({
      id: String(node.id),
      label: node.menu_name,
      level,
      isPermission: true,
      permissionName: node.permissionName,

      menuId: node.menu_id,
      menuActionId: node.menu_action_id,
      parentMenuActionId: node.parent_menu_action_id,
      isComponent: node.is_component,

      hasChildren: node.children?.length > 0,
    });

    if (node.children?.length) {
      rows.push(...this.getTreeRows(node.children, level + 1));
    }
  });

  return rows;
}


  assignablePayload: any = null;
  assignableMenuIds: number[] = [];

 loadAssignableMenus() {
  this.menuService.getMenus().subscribe({
    next: (res: any) => {
      const body = res?.body ?? res;

      this.assignablePayload = body ?? null;

      const data = body?.data ?? body;

      const ids: any = data?.main_menu_ids;

      this.assignableMenuIds = Array.isArray(ids) ? ids : [];

      // Set checked menus from API response
      this.assignableMenuIds.forEach((menuId: number) => {
        this.selectedMainMenus[menuId] = true;
      });
    },
    error: () => {
      this.assignablePayload = null;
      this.assignableMenuIds = [];
    },
  });
}

  
}



